
import { supabase } from '@/integrations/supabase/client';
import { Courier } from '@/services/types/courierTypes';
import { formatPhoneToE164 } from './courierBase';

/**
 * Получение данных курьера по ID
 * @param courierId - ID курьера из таблицы couriers
 * @returns Данные курьера или ошибка
 */
export const getCourierById = async (courierId: string) => {
  if (!courierId) {
    console.error('getCourierById: ID курьера не предоставлен');
    return { data: null, error: new Error('ID курьера не предоставлен') };
  }

  try {
    // Используем select('*') и limit(1) для избежания ошибки 406
    const { data: courierData, error: courierError } = await supabase
      .from('couriers')
      .select('*')
      .eq('id', courierId)
      .limit(1);
      
    if (courierError) {
      console.error('Ошибка при получении курьера по ID:', courierError);
      return { data: null, error: courierError };
    }
    
    // Проверяем наличие курьера в результатах запроса
    if (courierData && courierData.length > 0) {
      console.log('ПОЛУЧЕН КУРЬЕР ПО ID. ПРАВИЛЬНЫЙ ID КУРЬЕРА:', courierData[0].id);
      return { data: courierData[0], error: null };
    } else {
      return { data: null, error: new Error('Курьер не найден') };
    }
  } catch (error: any) {
    console.error('Ошибка получения данных курьера по ID:', error);
    return { data: null, error };
  }
};

/**
 * Получение данных курьера по телефону
 * @param phone - Телефон курьера
 * @returns Данные курьера или ошибка
 */
export const getCourierByPhone = async (phone: string) => {
  if (!phone) {
    console.error('getCourierByPhone: Номер телефона не предоставлен');
    return { data: null, error: new Error('Номер телефона не предоставлен') };
  }
  
  // Нормализация номера телефона для поиска
  const normalizedPhone = formatPhoneToE164(phone);
  
  // Удаляем + для совместимости с записями без +
  const phoneWithoutPlus = normalizedPhone.startsWith('+') 
    ? normalizedPhone.substring(1) 
    : normalizedPhone;
  
  console.log('Запрос на получение курьера. Оригинальный телефон:', phone);
  console.log('Нормализованный телефон с +:', normalizedPhone);
  console.log('Нормализованный телефон без +:', phoneWithoutPlus);
  
  // Тестовый номер для отладки
  if (phone === '+71234567890' || phone === '71234567890') {
    console.log('Найден тестовый номер телефона');
    const testUser = {
      id: '99b0de4a-1700-4404-8bc8-2addb287df5d', // корректный ID курьера
      name: 'Тестовый Курьер',
      email: 'test@example.com',
      phone: '+71234567890',
      status: 'active' as 'active', // Явно указываем тип
      role: 'courier',
      totalRentals: 5,
      registeredAt: new Date().toISOString(),
      avatar: null,
      tariff: 'Стандартный тариф',
      amo_contact_id: '12345',
      amo_deal_id: '67890',
      deal_number: 'TEST-001'
    };
    
    console.log('ID ТЕСТОВОГО КУРЬЕРА ИЗ ЗАПРОСА (правильный courier.id):', testUser.id);
    return { data: testUser, error: null };
  }

  try {
    // Используем select('*').limit(1) вместо maybeSingle() для избежания ошибки 406
    let { data: courierData, error: courierError } = await supabase
      .from('couriers')
      .select('*')
      .eq('phone', normalizedPhone)
      .limit(1);
      
    // Если ничего не нашли, пробуем без +
    if (!courierData || courierData.length === 0) {
      console.log('Поиск по телефону без "+": ', phoneWithoutPlus);
      
      const result = await supabase
        .from('couriers')
        .select('*')
        .eq('phone', phoneWithoutPlus)
        .limit(1);
        
      courierData = result.data;
      courierError = result.error;
    }
    
    if (courierError || !courierData || courierData.length === 0) {
      console.error('Не удалось найти курьера по телефону:', courierError || 'Курьер не найден');
      return { data: null, error: courierError || `Курьер с номером ${phone} не найден` };
    }

    // Выводим ID курьера из таблицы couriers (правильный courier.id)
    if (courierData && courierData.length > 0) {
      console.log('ID КУРЬЕРА ИЗ ТАБЛИЦЫ COURIERS НАЙДЕННОГО ПО ТЕЛЕФОНУ (правильный courier.id):', courierData[0].id);
    }

    // Возвращаем первый найденный результат или null
    return { 
      data: courierData && courierData.length > 0 ? courierData[0] : null, 
      error: null
    };
  } catch (error: any) {
    console.error('Ошибка получения данных курьера по телефону:', error);
    return { data: null, error };
  }
};

/**
 * Получение данных курьера по ID пользователя c использованием его телефона
 * @param userId - ID пользователя из auth.users
 * @returns Данные курьера или ошибка
 */
export const getCourierByUserId = async (userId: string) => {
  if (!userId) {
    console.error('getCourierByUserId: ID пользователя не предоставлен');
    return { data: null, error: new Error('ID пользователя не предоставлен') };
  }

  try {
    // Сначала проверим роль пользователя и получим его телефон из профиля
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('role, phone')
      .eq('id', userId)
      .single();
      
    if (profileError) {
      console.error('Ошибка при получении профиля пользователя:', profileError);
      return { data: null, error: profileError };
    }
    
    // Если пользователь не курьер, то не ищем его в таблице couriers
    if (profileData.role !== 'courier') {
      console.log('Пользователь не является курьером, роль:', profileData.role);
      return { data: null, error: new Error('Пользователь не является курьером') };
    }

    // Если есть телефон, ищем курьера по нему
    if (profileData.phone) {
      console.log('Поиск курьера по телефону из профиля:', profileData.phone);
      const result = await getCourierByPhone(profileData.phone);
      
      // Выводим ID курьера из запроса по userId
      if (result.data) {
        console.log('ID КУРЬЕРА ИЗ ЗАПРОСА ПО USER ID (правильный courier.id):', result.data.id);
      }
      
      return result;
    }
    
    // Если курьер не найден по телефону
    return { data: null, error: new Error('Курьер не найден') };
  } catch (error: any) {
    console.error('Ошибка получения данных курьера по ID пользователя:', error);
    return { data: null, error };
  }
};
